import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, Sparkles, CheckCircle2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { BookingForm } from "@/components/booking/BookingForm";
import { BookingChat } from "@/components/booking/BookingChat";
import { TYPE_META } from "@/components/booking/TypePicker";

const Done = ({ result, info }) => (
  <div data-testid="booking-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center space-y-3 fade-up">
    <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
    <h2 className="text-2xl font-display font-bold">Appuntamento confermato!</h2>
    <p className="text-slate-700">
      {new Date(result.start).toLocaleString("it-IT", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })} · {TYPE_META[result.type]?.label}
    </p>
    <p className="text-sm text-slate-500">{info.agent_name} ti aspetta. Se hai lasciato l'email riceverai una conferma.</p>
  </div>
);

export default function PublicBooking() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState("calendario");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/public/booking/${slug}`).then((r) => {
      setInfo(r.data);
      if (r.data.ai_enabled === false) setMode("calendario");
    }).catch(() => setError(true));
  }, [slug]);

  if (error) return <div data-testid="booking-unavailable" className="min-h-screen grid place-items-center text-slate-500 p-6 text-center">Pagina di prenotazione non disponibile.</div>;
  if (!info) return <div className="min-h-screen grid place-items-center text-slate-400">Caricamento…</div>;

  const tab = (id, label, I) => (
    <button data-testid={`mode-${id}`} onClick={() => setMode(id)}
      className={`flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium transition-colors ${mode === id ? "bg-[#0F172A] text-white" : "text-slate-600 hover:text-slate-900"}`}>
      <I className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-booking-page">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-3xl mx-auto px-6 pt-10 pb-16 flex items-center gap-5">
          {info.picture ? <img src={info.picture} alt="" className="h-16 w-16 rounded-full ring-4 ring-white/10" /> : <div className="h-16 w-16 rounded-full bg-sky-500 grid place-items-center text-2xl font-bold">{info.agent_name[0]}</div>}
          <div>
            <p className="text-sky-300 text-xs uppercase tracking-[0.2em]">Prenota un appuntamento</p>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="booking-agent-name">{info.agent_name}</h1>
            <p className="text-slate-300 text-sm mt-1">{info.intro || "Mutui, prestiti, cessione del quinto e polizze. Consulenza gratuita e senza impegno."}</p>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 -mt-8 pb-16 space-y-6">
        {result ? <Done result={result} info={info} /> : (
          <>
            {info.ai_enabled !== false && (
              <div className="flex gap-1 rounded-full bg-white border border-slate-200 shadow-sm p-1">
                {tab("calendario", "Scegli dal calendario", CalendarDays)}
                {tab("chat", "Chatta con l'assistente", Sparkles)}
              </div>
            )}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 fade-up">
              {mode === "chat" ? <BookingChat slug={slug} info={info} onBooked={setResult} /> : <BookingForm slug={slug} info={info} onBooked={setResult} />}
            </div>
          </>
        )}
        <p className="text-xs text-slate-400 flex items-center justify-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Durata {info.duration} minuti · I tuoi dati sono trattati in modo riservato</p>
      </main>
    </div>
  );
}
